/** @file Inserting one vertex: split an edge, split a triangle in 3, or extend past a hull edge. */
import type { MeshAtt } from '@pose2d/format/types';
import type { TopologyPlan, VertexSource } from './blendSources';
import { extendHullPlan } from './extendHullPlan';
import { insertParts } from './insertParts';

const px = (p: ArrayLike<number>, v: number): number => p[2 * v] ?? 0;
const py = (p: ArrayLike<number>, v: number): number => p[2 * v + 1] ?? 0;

/** Nearest triangle edge to (x, y) whose foot lies strictly inside it, with its parameter t along a → b. */
function nearestEdge(tris: readonly number[], p: ArrayLike<number>, x: number, y: number): { a: number; b: number; t: number; d: number } | null {
  let best: { a: number; b: number; t: number; d: number } | null = null;
  for (let i = 0; i < tris.length; i++) {
    const a = tris[i] ?? 0, b = tris[i % 3 === 2 ? i - 2 : i + 1] ?? 0;
    const ex = px(p, b) - px(p, a), ey = py(p, b) - py(p, a), len2 = ex * ex + ey * ey;
    const t = len2 > 0 ? ((x - px(p, a)) * ex + (y - py(p, a)) * ey) / len2 : -1;
    const d = Math.hypot(px(p, a) + t * ex - x, py(p, a) + t * ey - y);
    if (t > 0 && t < 1 && (best === null || d < best.d)) best = { a, b, t, d };
  }
  return best;
}

/** Splits every triangle on edge {a, b} at the new vertex. */
function splitEdge(mesh: MeshAtt, e: { a: number; b: number; t: number }): TopologyPlan {
  const tris: number[] = [];
  for (let t = 0; t < mesh.triangles.length; t += 3) {
    const tri = insertParts.triAt(mesh.triangles, t);
    const s = insertParts.sideOf(tri, e.a, e.b);
    tris.push(...(s === null ? tri : [s.u, -1, s.c, -1, s.w, s.c]));
  }
  const slot = insertParts.hullSlot(e.a, e.b, mesh.hull);
  const source: VertexSource = { kind: 'blend', parts: [[e.a, 1 - e.t], [e.b, e.t]] };
  return insertParts.planWith(mesh, slot ?? mesh.uvs.length / 2, source, tris, slot !== null);
}

/** The triangle containing (x, y) split in three around the new (last) vertex; null when (x, y) is outside. */
function splitInside(mesh: MeshAtt, p: ArrayLike<number>, x: number, y: number): TopologyPlan | null {
  for (let t = 0; t < mesh.triangles.length; t += 3) {
    const tri = insertParts.triAt(mesh.triangles, t);
    const w = insertParts.barycentric(p, tri, x, y);
    if (w === null || w.some((v) => v < -1e-9)) continue;
    const [a, b, c] = tri;
    const tris = [...mesh.triangles.slice(0, t), a, b, -1, b, c, -1, c, a, -1, ...mesh.triangles.slice(t + 3)];
    return insertParts.planWith(mesh, mesh.uvs.length / 2, { kind: 'blend', parts: [[a, w[0]], [b, w[1]], [c, w[2]]] }, tris, false);
  }
  return null;
}

/** True when some vertex of `p` lies within `tol` of (x, y) (the click means that vertex, not a new one). */
function nearVertex(p: ArrayLike<number>, count: number, x: number, y: number, tol: number): boolean {
  for (let v = 0; v < count; v++) if (Math.hypot(px(p, v) - x, py(p, v) - y) <= tol) return true;
  return false;
}

/**
 * Plan for a vertex at world (x, y) given the mesh's world points `p`: null within `tol` of a vertex (no zero-area
 * triangle); within `tol` of an edge it splits that edge (joining the hull on a hull edge), inside a triangle it splits
 * the triangle (appended as an interior vertex), outside it extends the hull (extendHullPlan); null when nothing applies.
 */
export function insertVertexPlan(mesh: MeshAtt, p: ArrayLike<number>, x: number, y: number, tol: number): TopologyPlan | null {
  if (nearVertex(p, mesh.uvs.length / 2, x, y, Math.max(tol, 1e-9))) return null;
  const edge = nearestEdge(mesh.triangles, p, x, y);
  if (edge !== null && edge.d <= tol) return splitEdge(mesh, edge);
  return splitInside(mesh, p, x, y) ?? extendHullPlan(mesh, p, x, y);
}
